import React, { useEffect, useState } from 'react';
import { Button, Table } from 'govuk-react-jsx';
import { useHistory, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getApplicationClaim, submitGrantApplication } from '../../firebase';
import {
  getBuildingTypeSelected,
  getBuildingAndStreet,
  getBuildingAndStreet2,
  getTownOrCity,
  getCounty,
  getPostCode,
  getLandlordOrPmcPrivateParking,
  getNumberOfChargepointsInstalled,
  getGrantTypeSelected,
  getSelectedGrantValue,
  getInstallerDetails,
  getNumberOfChargepointsSocketsInstalled,
  getStaffFleetChargepoints,
  getParkingTypesSelected,
} from '../../features/application/applicationJourneySlice';
import {
  getCompanyData,
  getBusinessVatData,
} from '../../features/registration/registrationJourneySlice';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import {
  getSubmittedApplicationId,
  getUserDetails,
  setSubmittedApplicationId,
} from '../../features/dashboard/dashboardSlice';
import useTitle from '../../hooks/useTitle';

const CheckYourPropertyAnswers = () => {
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  useTitle(
    'landlordApp.CheckYourPropertyAnswers.s9',
    'Check your answers - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );
  const companyDataRedux = useSelector(getCompanyData);
  const staffFleetChargepointsRedux = useSelector(getStaffFleetChargepoints);
  const parkingTypeSelectedRedux = useSelector(getParkingTypesSelected);
  const businessVatDataRedux = useSelector(getBusinessVatData);
  const buildingTypeRedux = useSelector(getBuildingTypeSelected);
  const buildingAndStreetRedux = useSelector(getBuildingAndStreet);
  const buildingAndStreetRedux2 = useSelector(getBuildingAndStreet2);
  const townOrCityRedux = useSelector(getTownOrCity);
  const countyRedux = useSelector(getCounty);
  const postCodeRedux = useSelector(getPostCode);
  const privateParkingRedux = useSelector(getLandlordOrPmcPrivateParking);
  const numberOfChargepoints = useSelector(getNumberOfChargepointsInstalled);
  const currentUserData = useSelector(getUserDetails);
  const grantTypeSelectedObjectRedux = useSelector(getSelectedGrantValue);
  const grantTypeSelectedRedux = useSelector(getGrantTypeSelected);
  const installerDetailsRedux = useSelector(getInstallerDetails);
  const numberOfChargepointSocketsRedux = useSelector(
    getNumberOfChargepointsSocketsInstalled
  );
  const history = useHistory();
  const dispatch = useDispatch();
  const applicationId = useSelector(getSubmittedApplicationId);
  const [clickedSubmitBtn, setClickedSubmitBtn] = useState(false);

  //  Avoids the user coming back and resubmitting the same application
  useEffect(() => {
    if (applicationId) {
      getApplicationClaim({ id: applicationId })
        .then((response) => {
          const { data } = response;
          if (data?.claim_status === 'Ready for Installer Claim') {
            window.location.assign('/dashboardHub/dashboard');
          }
        })
        .catch((error) => {
          if (error.code === 'functions/permission-denied') {
            window.location.assign('/dashboardHub/dashboard');
          }
        });
    }
  }, []);

  const handleBtn = () => {
    // prevent double clicking and multiple submissions
    setClickedSubmitBtn(true);
    if (privateParkingRedux === 'no') {
      history.push('/landlordApp/unable-to-proceed');
    } else if (
      staffFleetChargepointsRedux === 'No' &&
      grantTypeSelectedObjectRedux?.grantTypeShort === 'commercial properties'
    ) {
      history.push('/landlordApp/commercial/unable-to-proceed');
    } else {
      const installerName = `${installerDetailsRedux.installerDetails.primary_contact_firstname} ${installerDetailsRedux.installerDetails.primary_contact_lastname}`;
      submitGrantApplication({
        grantDocumentId: grantTypeSelectedRedux,
        isPrivateParking: privateParkingRedux,
        staffFleetChargepoints: staffFleetChargepointsRedux,
        parkingType: parkingTypeSelectedRedux,
        installerCompanyName:
          installerDetailsRedux.installerDetails?.company_name,
        installerName,
        installerFirstName:
          installerDetailsRedux.installerDetails.primary_contact_firstname,
        installerLastName:
          installerDetailsRedux.installerDetails.primary_contact_lastname,
        installerEmail: installerDetailsRedux.installerDetails?.email,
        installerNumber:
          installerDetailsRedux.installerDetails?.installer_number,
        installationAddressLine1: buildingAndStreetRedux,
        installationAddressLine2: buildingAndStreetRedux2,
        installationAddressCity: townOrCityRedux,
        installationAddressCounty: countyRedux,
        installationAddressPostcode: postCodeRedux,
        buildingType: buildingTypeRedux,
        numberOfChargepointsApplied: numberOfChargepoints,
        numberOfChargepointSocketsApplied: numberOfChargepointSocketsRedux,
      })
        .then((response) => {
          dispatch(setSubmittedApplicationId(response.data.id));
          history.push('/landlordApp/application-submitted');
        })
        .catch((error) => {
          if (error.code === 'functions/out-of-range') {
            history.push('/landlordApp/unable-to-start-new-application');
          }
          // TODO: Show a general error
        });
    }
  };

  const typeOfGrant =
    grantTypeSelectedObjectRedux.grantTypeShort === 'commercial properties'
      ? 'Commercial'
      : 'Residential';

  const buildingType =
    // eslint-disable-next-line
    buildingTypeRedux && buildingTypeRedux === 'detached-property'
      ? 'Detached property'
      : // eslint-disable-next-line
      buildingTypeRedux === 'terraced-property'
      ? 'Terraced property'
      : // eslint-disable-next-line
      buildingTypeRedux === 'individual-flat-or-apartment-building'
      ? 'Individual flat or apartment building'
      : buildingTypeRedux === 'semi-detached-property'
      ? 'Semi detached property'
      : buildingTypeRedux === 'bungalow' && 'Bungalow';

  const address = (
    <>
      <section>
        {buildingAndStreetRedux && `${buildingAndStreetRedux},`}
      </section>
      <section>
        {buildingAndStreetRedux2 && `${buildingAndStreetRedux2},`}
      </section>
      <section>{townOrCityRedux && `${townOrCityRedux},`}</section>
      <section>{countyRedux && `${countyRedux},`}</section>
      <section>{postCodeRedux && postCodeRedux}</section>
    </>
  );

  const pageModel = (
    <>
      <span className="govuk-caption-xl">
        {companyDataRedux && companyDataRedux.name}
        {businessVatDataRedux && businessVatDataRedux.target.name}
        {currentUserData != null &&
          currentUserData.business_name != null &&
          currentUserData.business_name}
      </span>
      <h1 className="govuk-heading-xl">
        {copyProvider.getCopy(
          'landlordApp.CheckYourPropertyAnswers.s10',
          'Check your answers'
        )}
      </h1>
      <Table
        className="govuk-summary-list govuk-!-margin-bottom-9"
        firstCellIsHeader
        rows={[
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'landlordApp.CheckYourPropertyAnswers.s11',
                  'Type of grant'
                ),
                format: 'numeric',
              },
              {
                children: typeOfGrant,
                format: 'text',
              },
              // NB: this empty object is required to keep table design consistant
              {},
            ],
          },
          typeOfGrant === 'Commercial'
            ? {
                cells: [
                  {
                    children: copyProvider.getCopy(
                      'landlordApp.CheckYourPropertyAnswers.s1a',
                      'Chargepoints for staff and fleet use only'
                    ),
                    format: 'text',
                  },
                  {
                    children: staffFleetChargepointsRedux,
                    format: 'text',
                  },
                  {
                    children: (
                      <Link
                        className="govuk-link"
                        to={{
                          pathname: '/landlordApp/commercial/staff-and-fleet',
                          state: { isFromCheckAnswers: true },
                        }}
                      >
                        {copyProvider.getCopy(
                          'landlordApp.CheckYourPropertyAnswers.s7',
                          'Change'
                        )}
                      </Link>
                    ),
                    format: 'numeric',
                  },
                ],
              }
            : {},
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'landlordApp.CheckYourPropertyAnswers.s12',
                  'Private parking'
                ),
                format: 'numeric',
              },
              {
                children:
                  privateParkingRedux && privateParkingRedux === 'yes'
                    ? 'Yes'
                    : 'No',
                format: 'text',
              },
              {
                children: (
                  <Link
                    className="govuk-link"
                    to={{
                      pathname: '/landlordApp/private-parking',
                      state: { isFromCheckAnswers: true },
                    }}
                  >
                    {copyProvider.getCopy(
                      'landlordApp.CheckYourPropertyAnswers.s7',
                      'Change'
                    )}
                  </Link>
                ),
                format: 'numeric',
              },
            ],
          },
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'landlordApp.CheckYourPropertyAnswers.s13',
                  'Installer name'
                ),
                format: 'numeric',
              },
              {
                children: installerDetailsRedux.installerDetails?.company_name,
                format: 'text',
              },
              {
                children: (
                  <Link
                    className="govuk-link"
                    to={{
                      pathname: '/landlordApp/installer-details',
                      state: { isFromCheckAnswers: true },
                    }}
                  >
                    {copyProvider.getCopy(
                      'landlordApp.CheckYourPropertyAnswers.s7',
                      'Change'
                    )}
                  </Link>
                ),
                format: 'numeric',
              },
            ],
          },
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'landlordApp.CheckYourPropertyAnswers.s14',
                  "Installer's OZEV number"
                ),
                format: 'numeric',
              },
              {
                children:
                  installerDetailsRedux.installerDetails?.installer_number,
                format: 'text',
              },
              {
                children: (
                  <Link
                    className="govuk-link"
                    to={{
                      pathname: '/landlordApp/installer-details',
                      state: { isFromCheckAnswers: true },
                    }}
                  >
                    {copyProvider.getCopy(
                      'landlordApp.CheckYourPropertyAnswers.s7',
                      'Change'
                    )}
                  </Link>
                ),
                format: 'numeric',
              },
            ],
          },
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'landlordApp.CheckYourPropertyAnswers.s15',
                  'Installation Address'
                ),
                format: 'numeric',
              },
              {
                children: address,
                format: 'text',
              },
              {
                children: (
                  <Link
                    className="govuk-link"
                    to={{
                      pathname: '/landlordApp/installation-address',
                      state: { isFromCheckAnswers: true },
                    }}
                  >
                    {copyProvider.getCopy(
                      'landlordApp.CheckYourPropertyAnswers.s7',
                      'Change'
                    )}
                  </Link>
                ),
                format: 'numeric',
              },
            ],
          },
          typeOfGrant === 'Commercial'
            ? {
                cells: [
                  {
                    children: copyProvider.getCopy(
                      'landlordApp.CheckYourPropertyAnswers.s4a',
                      'Type of car park'
                    ),
                    format: 'text',
                  },
                  {
                    children: parkingTypeSelectedRedux.map((value) => (
                      <section>{value}</section>
                    )),
                    format: 'text',
                  },
                  {
                    children: (
                      <Link
                        className="govuk-link"
                        to={{
                          pathname: '/landlordApp/parking-type',
                          state: { isFromCheckAnswers: true },
                        }}
                      >
                        {copyProvider.getCopy(
                          'landlordApp.CheckYourPropertyAnswers.s7',
                          'Change'
                        )}
                      </Link>
                    ),
                    format: 'numeric',
                  },
                ],
              }
            : {
                cells: [
                  {
                    children: copyProvider.getCopy(
                      'landlordApp.CheckYourPropertyAnswers.s5',
                      'Building type'
                    ),
                    format: 'numeric',
                  },
                  {
                    children: buildingType,
                    format: 'text',
                  },
                  {
                    children: (
                      <Link
                        className="govuk-link"
                        to={{
                          pathname: '/landlordApp/building-type',
                          state: { isFromCheckAnswers: true },
                        }}
                      >
                        {copyProvider.getCopy(
                          'landlordApp.CheckYourPropertyAnswers.s7',
                          'Change'
                        )}
                      </Link>
                    ),
                    format: 'numeric',
                  },
                ],
              },
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'landlordApp.CheckYourPropertyAnswers.s16',
                  'Number of chargepoints'
                ),
                format: 'numeric',
              },
              {
                children: numberOfChargepoints,
                format: 'text',
              },
              {
                children: (
                  <Link
                    className="govuk-link"
                    to={{ pathname: '/landlordApp/number-of-chargepoints' }}
                  >
                    {copyProvider.getCopy(
                      'landlordApp.CheckYourPropertyAnswers.s7',
                      'Change'
                    )}
                  </Link>
                ),
                format: 'numeric',
              },
            ],
          },
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'landlordApp.CheckYourPropertyAnswers.s17',
                  'Number of chargepoint sockets'
                ),
                format: 'numeric',
              },
              {
                children: numberOfChargepointSocketsRedux,
                format: 'text',
              },
              {
                children: (
                  <Link
                    className="govuk-link"
                    to={{ pathname: '/landlordApp/number-of-chargepoints' }}
                  >
                    {copyProvider.getCopy(
                      'landlordApp.CheckYourPropertyAnswers.s7',
                      'Change'
                    )}
                  </Link>
                ),
                format: 'numeric',
              },
            ],
          },
        ]}
      />
      <Button
        className="govuk-button"
        onClick={handleBtn}
        disabled={clickedSubmitBtn}
      >
        {copyProvider.getCopy(
          'landlordApp.CheckYourPropertyAnswers.s8',
          'Submit application'
        )}
      </Button>
    </>
  );

  return pageModel;
};

export default CheckYourPropertyAnswers;
